import type { CheckResult, LoanInput, MarketProduct } from "../types";
import { money, pct } from "./calc";

export type ScriptBeat = {
  id: string;
  title: string;
  say: string;
  hint: string;
};

export function stayScriptBeats(
  input: LoanInput,
  result: CheckResult,
  named: MarketProduct = result.best,
): ScriptBeat[] {
  const occupancy = input.occupancy === "owner" ? "owner-occupier" : "investment";
  const repay = input.repayment === "pi" ? "principal and interest" : "interest-only";
  const yearly = money(result.yearlySave);
  const gap = pct(input.rate - named.rate);

  const beats: ScriptBeat[] = [
    {
      id: "open",
      title: "Open with a named product",
      hint: "Retention only moves when you name a real print, not ‘the market’.",
      say: `Hi, I’ve just compared my home loan. The same kind of ${occupancy} ${repay} product is ${pct(named.rate)} with ${named.lender} (${named.product}, comparison rate ${pct(named.comparisonRate)}, up to ${named.maxLvr}% LVR${named.minLoan ? `, min loan $${named.minLoan.toLocaleString("en-AU")}` : ""}). That print is for new lending at that LVR — please check it on ${named.lender}’s rates page.`,
    },
    {
      id: "ask",
      title: "Ask to stay",
      hint: "Loyalty is the lever. Ask for the match in one sentence.",
      say: `I’d like to stay. I’ve got my accounts with you. That gap is ${gap} — about ${yearly} a year on my balance. Can you match ${pct(named.rate)}?`,
    },
    {
      id: "shave",
      title: "If they shave a little",
      hint: "Do not thank them and hang up. Push past the first cut.",
      say: `I appreciate the reduction, but at ${pct(named.rate)} I’d save thousands a year. I’d need you to get close to that for me to stay.`,
    },
    {
      id: "stall",
      title: "If they stall",
      hint: "The discharge request is what gets a supervisor on the line.",
      say: `That’s fine. Can you send me your discharge authority form? Email it or send the link. I’ll be refinancing.`,
    },
    {
      id: "retention",
      title: "When retention calls back",
      hint: "Give them a clean way to keep you today.",
      say: `I don’t want the hassle of moving, but ${pct(named.rate)} with ${named.lender} saves me ${yearly} a year. If you can get to that rate, or very close, I’ll cancel the discharge today. What’s the best you can actually do?`,
    },
  ];

  if (input.lender === "ANZ" || input.lender === "BOQ") {
    beats.push({
      id: "cash",
      title: "Ask for retention cash",
      hint: `${input.lender} has paid cash to keep customers. Ask once, in writing.`,
      say: `I know you offer retention cash to keep customers. If you can put around $2,000 on the table as well as the rate, we have a deal.`,
    });
  }

  beats.push({
    id: "close",
    title: "Close in writing",
    hint: "No rate is real until it is on the letter.",
    say: `Done. Send me that in writing today and cancel the discharge.`,
  });

  return beats;
}

export function stayScript(
  input: LoanInput,
  result: CheckResult,
  named: MarketProduct = result.best,
): string {
  return stayScriptBeats(input, result, named)
    .map((beat) => `${beat.title}\n${beat.say}`)
    .join("\n\n");
}
